import { getAccountId, leitenPrimitive, leitenRecord, leitenRequest } from "@helium10/re-core";
import { toastNotification } from "@helium10/re-ui-components";
import { orderBy } from "lodash-es";
import { create } from "zustand";

import { ApiAcceptFeeTermsInfo } from "../requests/acceptFeeTermsInfo";
import { ApiLandingPageInfo } from "../requests/landingPageInfo";
import { ApiRefundsServiceInfo } from "../requests/refundsServiceInfo";
import { SallerStatus } from "@/core/helpers/enum.helper";
import type { ApiRefundsServiceInfo as InboundApiRefundsServiceInfo } from "../components/InboundAccountDrawer/components/table/refundsServiceInfo";
import { ApiRefundsServiceInfo as InboundApiRefundsServiceInfoRequest } from "../components/InboundAccountDrawer/components/table/refundsServiceInfo";
import { useManageServiceInfoController } from "@helium10/re-features";

/**
 * 全局退款服务 Store
 * 合并了 main/store.ts 和 components/InboundAccountDrawer/refundStore.ts 中的相关字段
 */
export interface IRefundServiceStore {
  // ========== 从 main/store.ts 合并 ==========
  /** 服务信息（sellers 列表） */
  serviceInfo: ApiRefundsServiceInfo.IServiceInfo | null;
  /** 是否为老用户 */
  isOldUser: boolean;
  /** acceptInfo 接口是否已响应 */
  acceptInfoIsResponse: boolean;
  /** 费用条款接受信息 */
  acceptInfo: ApiAcceptFeeTermsInfo.IGetAcceptFeeTermsResponse;
  /** 接受费用条款的 POST 响应 */
  post: ApiAcceptFeeTermsInfo.IPostEnableAllResponse | null;
  /** setupInfo 接口是否已响应 */
  setupInfoIsResponse: boolean;
  /** 设置信息 */
  setupInfo: ApiLandingPageInfo.IGetSetupResponse;
  /** Landing 页面设置的 POST 响应 */
  setupPost: ApiLandingPageInfo.IPostSetupInfoResponse | null;

  // ========== 从 refundStore.ts 合并 ==========
  /** Inbound 服务信息（包含 enable、fullService 等状态） */
  iServiceInfo: InboundApiRefundsServiceInfo.IInboundResponse | null;
  /** InboundAccountDrawer 打开/关闭状态 */
  isOpen: boolean;
  /** 当前选中的 seller 信息 */
  currentInfo: Pick<
    InboundApiRefundsServiceInfo.IConnection,
    "sellerId" | "sellerAccount" | "enable" | "fullService"
  > | null;
  /** 更新 PackingList 状态的响应 */
  put: InboundApiRefundsServiceInfo.IUpdateResponse | null;
}

/**
 * 全局退款服务 Store
 * 使用 zustand.create，不使用 leitenFeature
 */
export const useRefundServiceStore = create<IRefundServiceStore>(() => ({
  // ========== 从 main/store.ts 合并 ==========
  serviceInfo: null,
  isOldUser: false,
  acceptInfoIsResponse: false,
  acceptInfo: {
    ifNeedAcceptance: false,
    ifAccept: false,
    ifInPromotion: false,
    ifAllSellersActivatedInbound: false,
  },
  post: null,
  setupInfoIsResponse: false,
  setupInfo: {
    ifSetup: false,
    setupTime: "",
  },
  setupPost: null,

  // ========== 从 refundStore.ts 合并 ==========
  iServiceInfo: null,
  isOpen: false,
  currentInfo: null,
  put: null,
}));

/**
 * InboundAccountDrawer 打开/关闭状态 Controller
 */
export const openController = leitenPrimitive(useRefundServiceStore, "isOpen");

/**
 * 当前选中的 seller 信息 Controller
 */
export const useCurrentInfoController = leitenRecord(useRefundServiceStore, "currentInfo");

/**
 * Inbound 服务信息 Controller
 * 调用 getInbound 接口获取包含 enable、fullService 等状态的服务信息
 */
export const useServiceInfoController = leitenRequest(
  useRefundServiceStore,
  "iServiceInfo",
  async (_: void) => {
    const accountId = getAccountId();
    return InboundApiRefundsServiceInfoRequest.getInbound({ accountId });
  },
  {
    initialStatus: "init",
  },
);

/**
 * 更新 PackingList 状态的 Controller
 * 更新完成后会刷新 iServiceInfo
 */
export const useUpdatePackingListStatusController = leitenRequest(
  useRefundServiceStore,
  "put",
  async (params: Pick<InboundApiRefundsServiceInfo.IConnection, "sellerId" | "enable">) => {
    const accountId = getAccountId();
    const result = await InboundApiRefundsServiceInfoRequest.updateStatus({ accountId, ...params });

    // 刷新 iServiceInfo
    useServiceInfoController.action();

    return result;
  },
  {
    initialStatus: "init",
  },
);

// ========== 从 main/store.ts 迁移的 Controller ==========
/**
 * 清理轮询定时器
 */
export const clearServiceInfoPolling = () => {
  if (serviceInfoPollingTimer) {
    clearTimeout(serviceInfoPollingTimer);
    serviceInfoPollingTimer = null;
  }
};

/**
 * 轮询定时器（用于 serviceInfo 的轮询）
 */
let serviceInfoPollingTimer: NodeJS.Timeout | null = null;

/**
 * 获取服务信息 Controller（带轮询，60秒）
 * 当有 seller 处于 "Lack of Permissions" 状态时，会自动轮询
 */
export const useGetServiceInfoWithPolling = leitenRequest(
  useRefundServiceStore,
  "serviceInfo",
  async (_?: void) => {
    if (serviceInfoPollingTimer) {
      clearTimeout(serviceInfoPollingTimer);
      serviceInfoPollingTimer = null;
    }
    const result = await ApiRefundsServiceInfo.get({});
    return result.data;
  },
  {
    initialStatus: "init",
    fulfilled: async ({ result: data }) => {
      // 是否存在 Lack of Permissions 状态的seller
      const isLackOfPermissions = data.sellers.some(
        (el) => el.statusValue === SallerStatus["Lack of Permissions"],
      );
      // 是否存在 Active 状态的seller
      const isActive = data.sellers.some((el) => el.statusValue === SallerStatus.Active);
      // 是否存在 Lack of Permissions 状态的seller 且不存在 Active 状态的seller，则开启轮询
      const hasWaiting = isLackOfPermissions && !isActive;
      if (hasWaiting) {
        serviceInfoPollingTimer = setTimeout(() => {
          useGetServiceInfoWithPolling.action();
        }, 60_000); // 60秒轮询
      }
      // 只要存在 Active 状态的seller，则清理轮询定时器，此时会自动跳转到 MRS 页面(在权限校验的文件 AuthGuard)，需要重新获取reminder的数据
      if (isActive) {
        clearServiceInfoPolling();
        useManageServiceInfoController.action({ forceRefresh: true });
      }
      // set newUser/oldUser
      const isOldUser =
        !!data.sellers.length &&
        data.sellers.some((el) => el.statusValue !== SallerStatus.Inactive);
      useRefundServiceStore.setState({ isOldUser });
    },
  },
);

/**
 * 获取服务信息 Controller（不带轮询）
 * 用于手动刷新或不需要轮询的场景
 * 统一使用 get 方法，支持 signal 参数
 * 合并了 useLocalManageServiceInfoController 的功能
 */
export const useGetServiceInfo = leitenRequest(
  useRefundServiceStore,
  "serviceInfo",
  async (_?: void) => {
    const result = await ApiRefundsServiceInfo.get({});
    return result.data;
  },
  {
    initialStatus: "init",
    fulfilled: async ({ result: data }) => {
      if (!data) return;
      // set newUser/oldUser
      const isOldUser =
        !!data.sellers.length &&
        data.sellers.some(
          (el: ApiRefundsServiceInfo.ISeller) => el.statusValue !== SallerStatus.Inactive,
        );
      useRefundServiceStore.setState({ isOldUser });
    },
  },
);

/**
 * 更新指定 seller 的服务信息（不带轮询）
 * 当携带 sellerId 参数时，只更新对应的 seller 数据
 */
export const useUpdateServiceInfoBySellerId = leitenRequest(
  useRefundServiceStore,
  "serviceInfo",
  async (params: { sellerId?: string }) => {
    const result = await ApiRefundsServiceInfo.get({ sellerId: params.sellerId });
    return result.data;
  },
  {
    initialStatus: "init",
    fulfilled: async ({ result: data, payload }) => {
      // 如果提供了 sellerId，只更新对应的 seller
      if (payload?.sellerId) {
        const currentState = useRefundServiceStore.getState();
        const currentServiceInfo = currentState.serviceInfo;
        if (currentServiceInfo) {
          const updatedSellers = currentServiceInfo.sellers.map((seller) => {
            const updatedSeller = data.sellers.find((s) => s.sellerId === seller.sellerId);
            return updatedSeller || seller;
          });
          useRefundServiceStore.setState({
            serviceInfo: {
              ...currentServiceInfo,
              sellers: updatedSellers,
            },
          });
          return;
        }
      }
      // 否则更新全量数据
      useRefundServiceStore.setState({ serviceInfo: data });
      // set newUser/oldUser
      const isOldUser =
        !!data.sellers.length &&
        data.sellers.some((el) => el.statusValue !== SallerStatus.Inactive);
      useRefundServiceStore.setState({ isOldUser });
    },
  },
);

/**
 * 获取费用条款接受信息 Controller
 */
export const useGetAcceptInfoController = leitenRequest(
  useRefundServiceStore,
  "acceptInfo",
  async (_: void) => {
    const accountId = getAccountId();
    const result = await ApiAcceptFeeTermsInfo.getAcceptInfo({ accountId });
    return result.data;
  },
  {
    initialStatus: "init",
    fulfilled: () => {
      useRefundServiceStore.setState({ acceptInfoIsResponse: true });
    },
  },
);

/**
 * 接受费用条款 Controller
 */
export const useAcceptFeeTermsController = leitenRequest(
  useRefundServiceStore,
  "post",
  async (params: { onSuccess: () => void }) => {
    const accountId = getAccountId();
    const result = await ApiAcceptFeeTermsInfo.acceptFeeTerms({ accountId, ifAccept: true });
    return result;
  },
  {
    initialStatus: "init",
    fulfilled: ({ payload }) => {
      payload.onSuccess();
      useGetAcceptInfoController.action();
    },
    rejected: ({ fetchError }) => {
      fetchError?.message && toastNotification.error(fetchError.message, { delay: 10 });
    },
  },
);

/**
 * 获取设置信息 Controller
 */
export const useGetSetupInfoController = leitenRequest(
  useRefundServiceStore,
  "setupInfo",
  async (params?: { nextLogic?: () => void } | void) => {
    const accountId = getAccountId();
    const result = await ApiLandingPageInfo.getSetupInfo({ accountId });
    return result.data;
  },
  {
    initialStatus: "init",
    fulfilled: ({ payload }) => {
      useRefundServiceStore.setState({ setupInfoIsResponse: true });
      payload?.nextLogic?.();
    },
  },
);

/**
 * Landing 页面设置 Controller
 */
export const useLandingPageSetupController = leitenRequest(
  useRefundServiceStore,
  "setupPost",
  async (params?: { nextLogic?: () => void } | void) => {
    const accountId = getAccountId();
    const result = await ApiLandingPageInfo.landingPageSetup({ accountId, ifSetup: true });
    return result;
  },
  {
    initialStatus: "init",
    fulfilled: ({ payload }) => {
      useGetSetupInfoController.action(payload);
    },
    rejected: ({ fetchError }) => {
      fetchError?.message && toastNotification.error(fetchError.message, { delay: 10 });
    },
  },
);

/**
 * 更新 serviceInfo 中指定 seller 的数据
 * 迁移自 refundStore.ts 的 updateSellerInMServiceInfo
 * @param sellerId - seller ID
 * @param updates - 要更新的 seller 字段
 */
export const updateSellerInServiceInfo = (
  sellerId: string,
  updates: Partial<ApiRefundsServiceInfo.ISeller>,
) => {
  const state = useRefundServiceStore.getState();
  const serviceInfo = state.serviceInfo;

  if (!serviceInfo) {
    return;
  }

  const updatedSellers = serviceInfo.sellers.map((seller) => {
    if (seller.sellerId === sellerId) {
      return {
        ...seller,
        ...updates,
      };
    }
    return seller;
  });

  useRefundServiceStore.setState({
    serviceInfo: {
      ...serviceInfo,
      sellers: updatedSellers,
    },
  });
};

/**
 * Connections Selector
 * 从 serviceInfo 中提取 sellers 列表，并添加 id 字段
 */
export const connectionsSelector = (state: IRefundServiceStore) => {
  const connections = state?.serviceInfo?.sellers.map((el) => ({ ...el, id: el.sellerId })) || [];
  return orderBy([...connections], "status", "asc");
};

/**
 * Inbound Connections Selector（合并版）
 * 合并 serviceInfo 和 iServiceInfo，添加 enable、fullService、sellerAccount 字段
 * 用于需要 Inbound 信息的场景（如 InboundAccountDrawer）
 */
export const inboundConnectionsSelector = (state: IRefundServiceStore) => {
  const serviceInfo = state?.serviceInfo;
  const iServiceInfo = state?.iServiceInfo;

  const connections =
    serviceInfo?.sellers.map((el) => ({
      ...el,
      id: el.sellerId,
      enable: iServiceInfo?.data?.find((item) => item.sellerId === el.sellerId)?.enable ?? false,
      fullService:
        iServiceInfo?.data?.find((item) => item.sellerId === el.sellerId)?.fullService ?? false,
      sellerAccount: el.sellerName ?? "",
    })) || [];

  return orderBy(connections, "status", "asc");
};
